<template>
  <div
    v-if="show"
    class="fixed z-[9999] inset-0 bg-slate-950/70 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
    @click.self="$emit('close')"
  >
    <div
      class="bg-white dark:bg-[#111C30] w-full max-w-4xl max-h-[92vh] rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-[#263449] relative animate-fade-in flex flex-col shadow-2xl overflow-hidden"
    >
      <!-- Modal Loading Overlay -->
      <AppLoader
        :isVisible="isSaving"
        variant="card"
        size="md"
        message="Saving Student Records..."
        subtext="Updating profile information, enrollment and documents"
        color="emerald"
      />

      <!-- Top Subtle Gradient Accent Line -->
      <div class="h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 shrink-0"></div>

      <!-- Professional Modal Header Bar -->
      <div
        class="bg-white dark:bg-[#111C30] px-6 py-4 border-b border-slate-100 dark:border-[#263449] flex items-center justify-between shrink-0"
      >
        <div>
          <div class="flex items-center gap-2.5 flex-wrap">
            <h2 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-tight">
              Edit Student Profile
            </h2>
            <span
              v-if="student?.rollNumber"
              class="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700/50"
            >
              Roll: {{ student.rollNumber }}
            </span>
          </div>
          <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-normal">
            Modifying records for {{ student?.name || "Student" }}
          </p>
        </div>

        <button
          @click="$emit('close')"
          :disabled="isSaving"
          class="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#18243A] transition-colors disabled:opacity-50"
          title="Close"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <!-- Navigation Segmented Tabs -->
      <div
        class="px-6 py-2 bg-slate-50/80 dark:bg-[#0B1120]/40 border-b border-slate-100 dark:border-[#263449] flex items-center gap-1.5 overflow-x-auto shrink-0"
      >
        <button
          type="button"
          v-for="tab in formTabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="[
            'px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-150 flex items-center gap-1.5',
            activeTab === tab.id
              ? 'bg-white dark:bg-[#18243A] text-slate-900 dark:text-white shadow-2xs border border-slate-200/80 dark:border-[#263449]'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-800/50',
          ]"
        >
          <component :is="tab.icon" class="w-3.5 h-3.5" />
          <span>{{ tab.label }}</span>
        </button>
      </div>

      <!-- Scrollable Form Body -->
      <div class="flex-1 overflow-y-auto p-5 sm:p-7 space-y-6 bg-slate-50/50 dark:bg-[#0B1120]">
        <!-- Error Banner -->
        <div
          v-if="saveError"
          class="p-4 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 rounded-2xl flex items-start gap-3"
        >
          <div class="p-1 bg-rose-100 dark:bg-rose-900/60 rounded-full text-rose-600 dark:text-rose-400 shrink-0">
            <AlertCircle class="w-4 h-4" />
          </div>
          <div class="flex-1 min-w-0">
            <h4 class="text-xs font-bold text-rose-800 dark:text-rose-200">Unable to Save</h4>
            <p class="text-xs text-rose-700 dark:text-rose-300 mt-0.5 break-words">{{ saveError }}</p>
          </div>
          <button
            @click="$emit('clear-error')"
            class="text-rose-400 hover:text-rose-600 p-1 rounded-lg"
          >
            <X class="w-4 h-4" />
          </button>
        </div>

        <!-- Success Banner -->
        <div
          v-if="saveSuccess"
          class="p-4 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/60 rounded-2xl flex items-start gap-3"
        >
          <div class="p-1 bg-emerald-100 dark:bg-emerald-900/60 rounded-full text-emerald-600 dark:text-emerald-400 shrink-0">
            <CheckCircle2 class="w-4 h-4" />
          </div>
          <div class="flex-1 min-w-0">
            <h4 class="text-xs font-bold text-emerald-800 dark:text-emerald-200">Success</h4>
            <p class="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">{{ saveSuccess }}</p>
          </div>
        </div>

        <!-- TAB 1: Basic & Contact Info -->
        <div
          v-show="activeTab === 'basic'"
          class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-5"
        >
          <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
            <User class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            <span>Personal & Contact Information</span>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            <!-- Full Name -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Full Name <span class="text-rose-500">*</span>
              </label>
              <input
                v-model="student.name"
                :disabled="isSaving"
                class="w-full px-3.5 py-2.5 border rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                :class="saveError && !student.name?.trim() ? 'border-rose-300 bg-rose-50/50' : 'border-slate-300 dark:border-[#263449]'"
                placeholder="Student full legal name"
              />
            </div>

            <!-- Roll Number -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Roll Number <span class="text-rose-500">*</span>
              </label>
              <input
                v-model="student.rollNumber"
                :disabled="isSaving"
                class="w-full px-3.5 py-2.5 border rounded-xl text-xs sm:text-sm font-mono font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                :class="saveError && !student.rollNumber?.trim() ? 'border-rose-300 bg-rose-50/50' : 'border-slate-300 dark:border-[#263449]'"
                placeholder="e.g. 101, A-12"
              />
            </div>

            <!-- Email Address -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Email Address <span class="text-slate-400 font-normal lowercase">(optional)</span>
              </label>
              <input
                v-model="student.email"
                type="email"
                :disabled="isSaving"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                placeholder="student@school.edu"
              />
            </div>

            <!-- Primary Phone -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Primary Phone <span class="text-rose-500">*</span>
              </label>
              <input
                v-model="student.phone"
                :disabled="isSaving"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-mono font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                placeholder="Primary contact number"
              />
            </div>

            <!-- Secondary Phone -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Secondary Phone <span class="text-slate-400 font-normal lowercase">(optional)</span>
              </label>
              <input
                v-model="student.secondaryPhone"
                :disabled="isSaving"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-mono font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                placeholder="Alternate or emergency contact"
              />
            </div>

            <!-- Date of Birth -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Date of Birth <span class="text-slate-400 font-normal lowercase">(optional)</span>
              </label>
              <input
                v-model="student.dob"
                type="date"
                :max="new Date().toISOString().split('T')[0]"
                :disabled="isSaving"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
              />
            </div>
          </div>
        </div>

        <!-- TAB 2: Academic Enrollment -->
        <div
          v-show="activeTab === 'academic'"
          class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-5"
        >
          <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
            <GraduationCap class="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <span>Academic & Enrollment Information</span>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            <!-- Class -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Class / Grade <span class="text-rose-500">*</span>
              </label>
              <select
                v-if="student.class"
                v-model="student.class._id"
                :disabled="isSaving"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
              >
                <option
                  v-for="cls in availableClasses"
                  :key="cls._id"
                  :value="cls._id"
                >
                  {{ cls.name }}{{ cls.section ? ` - ${cls.section}` : "" }}
                </option>
              </select>
            </div>

            <!-- Status -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Enrollment Status <span class="text-rose-500">*</span>
              </label>
              <select
                v-model="student.status"
                :disabled="isSaving"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
              >
                <option value="active">Active (Currently Enrolled)</option>
                <option value="leaved">InActive / On Leave</option>
                <option value="passout">Passout / Graduated</option>
              </select>
            </div>

            <!-- Enrollment Date -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Enrollment Date <span class="text-rose-500">*</span>
              </label>
              <input
                v-model="student.enrollmentDate"
                type="date"
                :disabled="isSaving"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
              />
            </div>

            <!-- Passout fields if applicable -->
            <template v-if="student.status === 'passout'">
              <div>
                <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                  Passout Session <span class="text-slate-400 font-normal lowercase">(optional)</span>
                </label>
                <input
                  v-model="student.session"
                  type="text"
                  placeholder="e.g. 2023-2024 / Spring"
                  :disabled="isSaving"
                  class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                />
              </div>

              <div>
                <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                  Passout Year
                </label>
                <input
                  v-model="student.passoutYear"
                  type="number"
                  placeholder="e.g. 2024"
                  :disabled="isSaving"
                  class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                />
              </div>
            </template>
          </div>
        </div>

        <!-- TAB 3: Address & Location -->
        <div
          v-show="activeTab === 'address'"
          class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-5"
        >
          <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
            <MapPin class="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span>Address & Residential Details</span>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            <!-- Pincode -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Pincode / Postal Code <span class="text-rose-500">*</span>
              </label>
              <input
                v-model="student.pincode"
                type="text"
                maxlength="6"
                :disabled="isSaving"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-mono font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                placeholder="6-digit pincode"
              />
            </div>

            <!-- City -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                City <span class="text-slate-400 font-normal lowercase">(optional)</span>
              </label>
              <input
                v-model="student.city"
                :disabled="isSaving"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                placeholder="City name"
              />
            </div>

            <!-- District / SubDistrict -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                District / Area <span class="text-slate-400 font-normal lowercase">(optional)</span>
              </label>
              <input
                v-model="student.subDistrict"
                :disabled="isSaving"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                placeholder="District or sub-district"
              />
            </div>

            <!-- State -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                State <span class="text-slate-400 font-normal lowercase">(optional)</span>
              </label>
              <input
                v-model="student.state"
                :disabled="isSaving"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                placeholder="State / Province"
              />
            </div>

            <!-- Full Address -->
            <div class="md:col-span-2">
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Full Residential Address <span class="text-slate-400 font-normal lowercase">(optional)</span>
              </label>
              <textarea
                v-model="student.address"
                rows="2"
                :disabled="isSaving"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition resize-none"
                placeholder="House / flat no, street, locality"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- TAB 4: Photo & Documents -->
        <div
          v-show="activeTab === 'documents'"
          class="space-y-5"
        >
          <!-- Student Photo Card -->
          <div class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
            <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
              <Camera class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span>Student Profile Photo</span>
            </div>

            <div class="flex flex-col sm:flex-row items-center gap-5">
              <!-- Current Photo / Placeholder -->
              <div class="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden ring-4 ring-slate-100 dark:ring-slate-800 shadow-md shrink-0 bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <img
                  v-if="student.photo"
                  :src="student.photo"
                  alt="Student Photo"
                  class="w-full h-full object-cover"
                />
                <User v-else class="w-10 h-10 text-slate-400" />
              </div>

              <!-- Upload & Controls -->
              <div class="flex-1 space-y-2 w-full text-center sm:text-left">
                <div class="flex items-center gap-2 justify-center sm:justify-start">
                  <label class="cursor-pointer inline-flex items-center gap-1.5 px-3.5 py-2 bg-indigo-50 dark:bg-indigo-950/60 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 rounded-xl text-xs font-semibold border border-indigo-200 dark:border-indigo-800 transition">
                    <Upload class="w-3.5 h-3.5" />
                    <span>Upload New Photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      @change="$emit('photo-upload', $event)"
                      :disabled="isSaving"
                      class="hidden"
                    />
                  </label>
                  <button
                    v-if="student.photo"
                    type="button"
                    @click="
                      student.photo = null;
                      $emit('photo-upload', null);
                    "
                    :disabled="isSaving"
                    class="px-3 py-2 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-xl text-xs font-semibold transition"
                  >
                    Remove
                  </button>
                </div>
                <p class="text-[11px] text-slate-400 dark:text-slate-500">
                  Supported formats: JPG, PNG, WEBP (Max size: 5MB)
                </p>
              </div>
            </div>
          </div>

          <!-- Documentation Records Card -->
          <div class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
            <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
              <FileText class="w-4 h-4 text-violet-600 dark:text-violet-400" />
              <span>Identity & Academic Documents</span>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Aadhaar Card -->
              <div class="p-4 rounded-xl border border-slate-200/80 dark:border-[#263449] bg-slate-50/50 dark:bg-[#0F172A] space-y-2.5">
                <div class="flex items-center justify-between">
                  <span class="text-xs font-bold text-slate-800 dark:text-white">Aadhaar Card</span>
                  <span
                    v-if="student.aadhaarCard"
                    class="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full"
                  >
                    <Check class="w-3 h-3" /> Uploaded
                  </span>
                </div>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="$emit('document-upload', $event, 'aadhaarCard')"
                  :disabled="isSaving"
                  class="w-full text-xs text-slate-500 file:mr-2.5 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
              </div>

              <!-- Birth Certificate -->
              <div class="p-4 rounded-xl border border-slate-200/80 dark:border-[#263449] bg-slate-50/50 dark:bg-[#0F172A] space-y-2.5">
                <div class="flex items-center justify-between">
                  <span class="text-xs font-bold text-slate-800 dark:text-white">Birth Certificate</span>
                  <span
                    v-if="student.birthCertificate"
                    class="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full"
                  >
                    <Check class="w-3 h-3" /> Uploaded
                  </span>
                </div>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="$emit('document-upload', $event, 'birthCertificate')"
                  :disabled="isSaving"
                  class="w-full text-xs text-slate-500 file:mr-2.5 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
              </div>

              <!-- Transfer Certificate -->
              <div class="p-4 rounded-xl border border-slate-200/80 dark:border-[#263449] bg-slate-50/50 dark:bg-[#0F172A] space-y-2.5">
                <div class="flex items-center justify-between">
                  <span class="text-xs font-bold text-slate-800 dark:text-white">Transfer Certificate</span>
                  <span
                    v-if="student.transferCertificate"
                    class="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full"
                  >
                    <Check class="w-3 h-3" /> Uploaded
                  </span>
                </div>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="$emit('document-upload', $event, 'transferCertificate')"
                  :disabled="isSaving"
                  class="w-full text-xs text-slate-500 file:mr-2.5 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
              </div>

              <!-- Marksheet -->
              <div class="p-4 rounded-xl border border-slate-200/80 dark:border-[#263449] bg-slate-50/50 dark:bg-[#0F172A] space-y-2.5">
                <div class="flex items-center justify-between">
                  <span class="text-xs font-bold text-slate-800 dark:text-white">Marksheet</span>
                  <span
                    v-if="student.marksheet"
                    class="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full"
                  >
                    <Check class="w-3 h-3" /> Uploaded
                  </span>
                </div>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="$emit('document-upload', $event, 'marksheet')"
                  :disabled="isSaving"
                  class="w-full text-xs text-slate-500 file:mr-2.5 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
              </div>
            </div>
            <p class="text-[11px] text-slate-400 dark:text-slate-500">
              Supported formats: PDF, JPG, PNG (Max 10MB per file)
            </p>
          </div>
        </div>
      </div>

      <!-- Sticky Footer Actions -->
      <div
        class="p-4 sm:p-5 border-t border-slate-200/80 dark:border-[#263449] bg-white dark:bg-[#111C30] flex items-center justify-between shrink-0"
      >
        <span class="text-[11px] text-slate-400 dark:text-slate-500 hidden sm:inline">
          Required fields are marked with <span class="text-rose-500 font-bold">*</span>
        </span>
        <div class="flex items-center gap-2.5 w-full sm:w-auto justify-end">
          <button
            type="button"
            @click="$emit('close')"
            :disabled="isSaving"
            class="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="$emit('save')"
            :disabled="isSaving"
            class="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold shadow-sm transition disabled:opacity-50"
          >
            <Save class="w-3.5 h-3.5" />
            <span>{{ isSaving ? "Saving..." : "Save Changes" }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed } from "vue";
  import {
    X,
    Save,
    User,
    GraduationCap,
    MapPin,
    Camera,
    Upload,
    FileText,
    Check,
    AlertCircle,
    CheckCircle2,
  } from "lucide-vue-next";
  import { useStore } from "vuex";

  const store = useStore();

  defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    student: {
      type: Object,
      default: () => ({}),
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    saveError: {
      type: String,
      default: "",
    },
    saveSuccess: {
      type: String,
      default: "",
    },
  });

  defineEmits([
    "close",
    "save",
    "photo-upload",
    "document-upload",
    "clear-error",
  ]);

  const activeTab = ref("basic");

  const formTabs = [
    { id: "basic", label: "Personal & Contact", icon: User },
    { id: "academic", label: "Academic Info", icon: GraduationCap },
    { id: "address", label: "Address & Location", icon: MapPin },
    { id: "documents", label: "Photo & Documents", icon: FileText },
  ];

  const availableClasses = computed(() => store.getters.allClasses || []);

  onMounted(() => {
    store.dispatch("fetchAllClasses");
  });
</script>

<style scoped>
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: scale(0.97);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  .animate-fade-in {
    animation: fade-in 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }
</style>
